(() => {
  'use strict';
  class DropkyPriceEater extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      try { this.frames = JSON.parse(this.querySelector('[data-price-frames]')?.textContent || '[]'); }
      catch { this.frames = []; }
      if (!Array.isArray(this.frames) || !this.frames.length) return;
      this.index = 0;
      this.userPaused = false;
      this.complete = false;
      this.inView = !window.IntersectionObserver;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseButton = this.querySelector('[data-pause]');
      this.controls = this.querySelector('[data-motion-controls]');
      this.querySelector('[data-replay]')?.addEventListener('click', () => this.restart(), options);
      this.pauseButton?.addEventListener('click', () => {
        if (this.complete) { this.restart(); return; }
        this.userPaused = !this.userPaused;
        this.update();
      }, options);
      this.querySelectorAll('[data-frame-index]').forEach(button => button.addEventListener('click', () => {
        this.applyFrame(Number(button.dataset.frameIndex));
        this.restart();
      }, options));
      this.querySelector('[data-frame-high]')?.addEventListener('animationiteration', event => {
        if (event.animationName !== 'dk-price-feed') return;
        if (this.dataset.loop !== 'true') {
          this.complete = true;
          this.dataset.completed = 'true';
          this.update();
          return;
        }
        this.applyFrame((this.index + 1) % this.frames.length);
      }, options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.motion.addEventListener('change', () => this.update(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.frames.findIndex(frame => frame.id === event.detail?.blockId);
        if (index < 0 || (!this.contains(event.target) && event.target !== this.closest('.shopify-section'))) return;
        this.applyFrame(index);
        this.restart(false);
        this.userPaused = true;
        this.update();
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.contains(event.target) && event.target !== this.closest('.shopify-section')) return;
        this.userPaused = false;
        this.update();
      }, options);
      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.update();
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }
      this.applyFrame(0);
      this.update();
    }
    applyFrame(index) {
      const frame = this.frames[index];
      if (!frame || typeof frame !== 'object') return;
      this.index = index;
      for (const [selector, key] of [['[data-frame-high]', 'high'], ['[data-frame-low]', 'low'], ['[data-frame-result]', 'low'], ['[data-frame-title]', 'title'], ['[data-frame-note]', 'note'], ['[data-frame-savings]', 'savings']]) {
        const node = this.querySelector(selector);
        if (node) node.textContent = typeof frame[key] === 'string' ? frame[key] : '';
      }
      const savings = this.querySelector('[data-frame-savings]');
      if (savings) savings.hidden = !frame.savings;
      const anchor = this.querySelector('[data-frame-link]');
      if (anchor) {
        try {
          const url = new URL(frame.url, location.origin);
          if (url.origin === location.origin && /(?:^|\/)(products|collections)\//.test(url.pathname)) anchor.href = url.href;
        } catch { /* Keep the server-rendered product link. */ }
      }
      this.querySelectorAll('[data-frame-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.frameIndex) === index)));
    }
    restart(announce = true) {
      this.complete = false;
      this.userPaused = false;
      delete this.dataset.completed;
      this.classList.remove('is-enhanced');
      // Flush only on an explicit replay so all CSS tracks start together.
      void this.offsetWidth;
      this.update();
      if (announce) {
        const status = this.querySelector('[data-scene-status]');
        if (status) status.textContent = `${this.frames[this.index]?.title || ''}. ${this.frames[this.index]?.low || ''}.`;
      }
    }
    update() {
      const enabled = this.dataset.animation === 'true' && !this.motion.matches;
      this.classList.toggle('is-enhanced', enabled);
      this.dataset.playing = String(enabled && this.inView && !document.hidden && !this.userPaused && !this.complete);
      if (this.controls) this.controls.hidden = !enabled;
      if (this.pauseButton) {
        const paused = this.userPaused || this.complete;
        this.pauseButton.textContent = paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
        this.pauseButton.setAttribute('aria-pressed', String(paused));
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }
  }
  if (!customElements.get('dk-price-eater')) customElements.define('dk-price-eater', DropkyPriceEater);
})();
